import React, { RefObject } from "react"
import { TourStep } from "../TourDocument"
import * as FFIcons from "../../../../libs/corporate/icons/outlined/other/FileAndFolderCollection"
import * as ActionIcons from "../../../../libs/corporate/icons/outlined/editor/ActionCollection"
import * as MainIcons from "../../../../libs/corporate/icons/outlined/action/MainCollection"
import * as RotationIcons from "../../../../libs/corporate/icons/outlined/directions/RotationCollection"
import { EmptySquareIcon } from "../../../../libs/corporate/icons/outlined/suggested/SquareCollection"
import { AdditionalIcon } from "../../../../libs/corporate/icons/outlined/other/FileAndFolderCollection"

export interface ContractorPluginViewTourRefs {
    fileAndFolderButton: RefObject<HTMLDivElement>
    treeCommands: RefObject<HTMLDivElement>
    treeSearch: RefObject<HTMLDivElement>
    treeList: RefObject<HTMLDivElement>
    commandsPanel: RefObject<HTMLDivElement>
    showDeleted: RefObject<HTMLDivElement>
    additionalFilters: RefObject<HTMLDivElement>
    convolution: RefObject<HTMLDivElement>
    search: RefObject<HTMLDivElement>
    totalCount: RefObject<HTMLDivElement>
    grid: RefObject<HTMLDivElement>
    stores: RefObject<HTMLDivElement>
    addEdit: RefObject<HTMLDivElement>
}

export interface ContractorPluginViewTourActions {
    groupButton: RefObject<HTMLDivElement>
}

const iconRowStyle = { display: "flex", alignItems: "center", margin: "5px", gap: "10px" }

const waitFor = (condition: () => boolean, timeout = 2000) => {
    return new Promise<boolean>((resolve) => {
        const start = Date.now()

        const check = () => {
            if (condition()) {
                resolve(true)
                return
            }

            if (Date.now() - start >= timeout) {
                resolve(false)
                return
            }

            window.requestAnimationFrame(check)
        }

        check()
    })
}

const isVisible = (element: HTMLElement | null) => {
    return Boolean(element && element.isConnected && element.getClientRects().length > 0 && window.getComputedStyle(element).display !== "none")
}

const clickGroupButton = (actions: ContractorPluginViewTourActions) => {
    actions.groupButton.current?.querySelector<HTMLButtonElement>("button")?.click()
}

const ensureTreeOpened = async (refs: ContractorPluginViewTourRefs, actions: ContractorPluginViewTourActions) => {
    if (!isVisible(refs.treeList.current)) {
        clickGroupButton(actions)
    }

    const isTreeOpened = await waitFor(() => isVisible(refs.treeList.current))

    if (!isTreeOpened) {
        throw new Error("Contractor tour: tree panel was not opened")
    }
}

const ensureTreeClosed = async (refs: ContractorPluginViewTourRefs, actions: ContractorPluginViewTourActions) => {
    if (isVisible(refs.treeList.current)) {
        clickGroupButton(actions)
    }

    const isTreeClosed = await waitFor(() => !isVisible(refs.treeList.current))

    if (!isTreeClosed) {
        throw new Error("Contractor tour: tree panel was not closed")
    }
}

export const getContractorPluginViewTourSteps = (
    refs: ContractorPluginViewTourRefs,
    actions: ContractorPluginViewTourActions,
): TourStep[] => [
    {
        target: () => refs.fileAndFolderButton.current,
        description: "Отображение контрагентов по\nгруппам.",
        placement: "bottom",
        onNext: async () => {
            await ensureTreeOpened(refs, actions)
        },
    },
    {
        target: () => refs.treeCommands.current,
        keepIfHidden: true,
        onEnter: async () => {
            await ensureTreeOpened(refs, actions)
        },
        description: (
            <div>
                Панель управления группами контрагентов:
                <div style={iconRowStyle}>
                    <RotationIcons.RotationRightIcon /> Сбросить - убрать выделение с текущей группы и отобразить всех контрагентов
                </div>
                <div style={iconRowStyle}>
                    <FFIcons.FilePlusIcon /> Добавить группу
                </div>
                <div style={iconRowStyle}>
                    <FFIcons.FolderIcon /> Изменить группу
                </div>
                <div style={iconRowStyle}>
                    <FFIcons.FolderMinusIcon /> Удалить группу
                </div>
                Если сначала выделить существующую группу, а затем нажать «Добавить группу», то выделенная группа будет считаться родительской для новой.
            </div>
        ),
        placement: "right",
    },
    {
        target: () => refs.treeSearch.current,
        keepIfHidden: true,
        onEnter: async () => {
            await ensureTreeOpened(refs, actions)
        },
        description: "Строка поиска по группам контрагентов",
        placement: "right",
    },
    {
        target: () => refs.treeList.current,
        keepIfHidden: true,
        onEnter: async () => {
            await ensureTreeOpened(refs, actions)
        },
        description: "Список-«дерево» групп контрагентов.",
        placement: "right",
        onNext: async () => {
            await ensureTreeClosed(refs, actions)
        },
    },
    {
        target: () => refs.commandsPanel.current,
        onEnter: async () => {
            await ensureTreeClosed(refs, actions)
        },
        description: (
            <div>
                Панель также содержит стандартные команды управления:
                <div style={iconRowStyle}>
                    <FFIcons.FilePlusIcon /> Добавить запись
                </div>
                <div style={iconRowStyle}>
                    <ActionIcons.HighlightIcon /> Редактировать запись
                </div>
                <div style={iconRowStyle}>
                    <ActionIcons.CopyIcon /> Копировать запись
                </div>
                <div style={iconRowStyle}>
                    <MainIcons.Delete2Icon /> Удалить запись
                </div>
                <div style={iconRowStyle}>
                    <MainIcons.DeleteIcon /> Восстановить запись
                </div>
                <div style={iconRowStyle}>
                    <MainIcons.SearchIconPanel /> Поиск
                </div>
                <div style={iconRowStyle}>
                    <RotationIcons.RotationRightIcon /> Обновить
                </div>
                <div style={iconRowStyle}>
                    <EmptySquareIcon /> <AdditionalIcon /> Групповая обработка
                </div>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => refs.showDeleted.current,
        description: (
            <div>
                Дополнительные фильтры отображения данных:
                <ul>
                    <li>Удалённые - отображать удалённые записи</li>
                </ul>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => refs.additionalFilters.current,
        description: (
            <div>
                Дополнительные фильтры отображения данных:
                <ul>
                    <li>Собственные контрагенты - отображать только контрагентов, привязанных к пользователю</li>
                    <li>Собственные юр. лица - отображать только тех контрагентов, чьи юр. лица совпадают с юр. лицами контрагентов, прикреплённых к пользователю.</li>
                </ul>
            </div>
        ),
        placement: "bottom",
    },
    {
        target: () => refs.convolution.current,
        description: "Свёртка товаров - объединение двух позиций в одну. Для свёртки необходимо нажать иконку «Групповая обработка», затем выделить две записи, нажать кнопку «Свёртка контрагентов» и подтвердить выбор.",
        placement: "bottom",
    },
    {
        target: () => refs.search.current,
        description: "Строка поиска контрагентов.",
        placement: "bottom",
    },
    {
        target: () => refs.totalCount.current,
        description: "Здесь отображается\nколичество отображаемых на данный момент записей.",
        placement: "bottom",
    },
    {
        target: () => refs.grid.current,
        description: "Таблица со списком контрагентов",
        placement: "top",
    },
    {
        target: () => refs.stores.current,
        description: "Список складов выделенного контрагента",
        placement: "top",
    },
    {
        target: () => refs.addEdit.current,
        description: (
            <div>
                Для создание нового контрагента нажмите кнопку:
                <div style={iconRowStyle}>
                    <FFIcons.FilePlusIcon /> Добавить запись
                </div>
                При нажатии:
                <div style={iconRowStyle}>
                    <ActionIcons.HighlightIcon /> Редактировать запись
                </div>
                открывается форма выбранного контрагента для редактирования
            </div>
        ),
        placement: "bottom",
    },
]
